/**
 * iter-120 round-4 follow-up #12 — Shared SSO provider helpers.
 *
 * Centralises the provider label, icon, and tenant-enabled-providers
 * lookup so the InviteUserDialog, EditUserDialog, UserTables, and
 * AcceptInvite page all render the same iconography and labels.
 */
import React from "react";
import api from "@/lib/api";

/** Human-readable label for a provider key. */
export function ssoProviderLabel(key) {
    if (key === "microsoft") return "Microsoft";
    if (key === "google") return "Google";
    return "";
}

/**
 * Inline-SVG icon for a provider — sized 14×14 by default to fit
 * inside table cells and select triggers. No external assets needed
 * so this works offline + in print preview.
 */
export function SsoProviderIcon({ provider, size = 14, className = "" }) {
    if (provider === "microsoft") {
        return (
            <svg
                width={size} height={size} viewBox="0 0 16 16"
                aria-label="Microsoft" className={className}
                data-testid={`sso-icon-microsoft`}
            >
                <rect width="7" height="7" fill="#F25022" />
                <rect x="8" width="7" height="7" fill="#7FBA00" />
                <rect y="8" width="7" height="7" fill="#00A4EF" />
                <rect x="8" y="8" width="7" height="7" fill="#FFB900" />
            </svg>
        );
    }
    if (provider === "google") {
        return (
            <svg
                width={size} height={size} viewBox="0 0 48 48"
                aria-label="Google" className={className}
                data-testid={`sso-icon-google`}
            >
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
                <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2.1 1.4-4.7 2.3-7.3 2.3-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.2c-.4.4 6.5-4.8 6.5-14.7 0-1.3-.1-2.3-.4-3.5z" />
            </svg>
        );
    }
    return null;
}

/**
 * Compact "icon + label" badge used in tables and select triggers.
 * Falls back to plain "SSO" when the provider isn't recognised yet
 * (e.g. legacy users with `sso_required=true` but `sso_provider=null`).
 */
export function SsoProviderBadge({ provider, className = "", testid }) {
    const label = ssoProviderLabel(provider);
    if (!label) {
        return (
            <span
                className={`inline-flex items-center gap-1 text-[11px] font-mono ${className}`}
                data-testid={testid}
            >
                SSO
            </span>
        );
    }
    return (
        <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-mono ${className}`}
            data-testid={testid}
        >
            <SsoProviderIcon provider={provider} />
            {label}
        </span>
    );
}

/**
 * Fetch enabled SSO providers. Returns an array like `["microsoft"]`,
 * `["google"]`, `["microsoft", "google"]`, or `[]` if SSO isn't set up.
 *
 * • No args → the CALLER's own tenant (legacy behaviour, via /settings).
 * • `{ orgId, tenantId }` → the invite TARGET tenant's own SSO config
 *   (server-authorised: SA any tenant, OA tenants in own org, EA own).
 */
export async function fetchTenantSsoProviders(target) {
    if (target) {
        try {
            const params = {};
            if (target.orgId) params.org_id = target.orgId;
            if (target.tenantId) params.tenant_id = target.tenantId;
            const r = await api.get("/auth/invite/sso-providers", { params });
            const d = (r.data && (r.data.data || r.data)) || {};
            return Array.isArray(d.providers) ? d.providers : [];
        } catch {
            return [];
        }
    }
    try {
        // The full settings doc includes `sso_v2` after scrubbing the
        // encrypted secrets — exactly what we need to know which
        // providers are usable for new invitations.
        const r = await api.get("/settings");
        const sso = (r.data && r.data.sso_v2) || {};
        const out = [];
        for (const p of ["microsoft", "google"]) {
            const cfg = sso[p] || {};
            if (cfg.enabled && cfg.status === "configured") out.push(p);
        }
        return out;
    } catch {
        return [];
    }
}
